"use client";
import { useState } from "react";
import { format } from "date-fns";
import {
  convertToZone,
  formatCOP,
} from "../../utils/formatters";
import { SalesTableProps } from "./SalesInterface";
import { ButtonsFilters } from "../filters/ButtonsFilters";
import { PaidState } from "../../types/order/paidState";
import { LogisticState } from "../../types/order/logisticState";
import { downloadOrders, getOrderDetails, deleteOrder } from "../../services/orders/orderAxios";
import { useCurrency } from "../../context/Currency";

import { dictionary } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import { DefaultButton } from "../buttons/DefaultButton";
import { PaymentProvider } from "../../pages/SalesPage/SalesPageInterface";
import { IconTrash } from "../../assets/icons/icons";
import { BaseModal } from "../modals/BaseModal";
import { handleApi } from "../../utils/handleApi";
import { Order } from "../../pages/SalesPage/SalesPageInterface";
import { toast } from "react-hot-toast";

const SalesTable = ({
  filterOrders,
  orders,
  activeFilter,
  onOrderDeleted,
}: SalesTableProps) => {
  const { language } = useAuth();
  const [isDownloading, setIsDownloading] = useState(false);
  const { isDollar } = useCurrency();
  const [openModalDelete, setOpenModalDelete] = useState<boolean>(false);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);

  const handleDownloadOrders = async () => {
    setIsDownloading(true);
    const orders = await downloadOrders();
    window.open(orders.s3_url, "_blank");
    setIsDownloading(false);
  };

  const paymentProviderLabels: Record<PaymentProvider, string> = {
    [PaymentProvider.CONTRA_ENTREGA]: "Contra Entrega",
    [PaymentProvider.MERCADO_PAGO]: "Mercado Pago",
    [PaymentProvider.STRIPE]: "Stripe",
  };

  const [deleteOrderMessage, setDeleteOrderMessage] = useState<string>(dictionary["are-you-sure-you-want-to-delete-this-order"][language]);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState<boolean>(false);

  const handleDeleteOrder = async (order: Order) => {
    setOrderToDelete(order);
    setIsLoadingOrderDetails(true);
    setOpenModalDelete(true);
    
    const {res, error} = await handleApi(getOrderDetails(order.hash_id));
    if (error) {
      console.error("Error al obtener los detalles de la orden:", error);
      setDeleteOrderMessage(dictionary["error-getting-order-details"][language]);
      setIsLoadingOrderDetails(false);
      setOpenModalDelete(false);
      return;
    }

    if (res?.order?.dropi_state) {
      setDeleteOrderMessage(dictionary["this-order-is-integrated-with-dropi"][language]);
    } else {
      setDeleteOrderMessage(dictionary["are-you-sure-you-want-to-delete-this-order"][language]);
    }
    setIsLoadingOrderDetails(false);
  };

  const closeModal = () => {
    setOpenModalDelete(false);
    setOrderToDelete(null);
    setIsLoadingOrderDetails(false);
    setDeleteOrderMessage(dictionary["are-you-sure-you-want-to-delete-this-order"][language]);
  };

  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;

    setIsLoadingOrderDetails(true);
    const {res, error} = await handleApi(deleteOrder(orderToDelete.id));
    if (error) {
      console.error("Error al eliminar la orden:", error);
      toast.error(dictionary["error-deleting-order"][language]);
      setIsLoadingOrderDetails(false);
      return;
    }
    toast.success(dictionary["order-deleted"][language]);
    onOrderDeleted(orderToDelete.id.toString());
    setIsLoadingOrderDetails(false);
    closeModal();
  };


  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {openModalDelete && (
        <BaseModal
          isOpen={openModalDelete}
          onClose={closeModal}
          imageSrc="https://fluxi.s3.amazonaws.com/prod/assets/media/warning-purple.gif"
          imageAlt="Warning"
          title="Eliminar orden"
          description={deleteOrderMessage}
          buttonOne={{
            text: "Eliminar",
            onClick: confirmDeleteOrder,
            loading: isLoadingOrderDetails,
            disabled: isLoadingOrderDetails,
          }}
          buttonTwo={{
            text: "Cancelar",
            onClick: closeModal,
          }}
        />
      )}
      <div className="flex justify-between items-center">
        <ButtonsFilters
          filterOrders={filterOrders}
          activeFilter={activeFilter}
        />

        <DefaultButton
          title={
            isDownloading
              ? dictionary["downloading"][language]
              : dictionary["download-orders"][language]
          }
          onClick={handleDownloadOrders}
          isLoading={isDownloading}
          className="mx-4"
        />
        {/* <button onClick={handleDownloadOrders} className="bg-blue-500 text-white px-4 py-2 rounded-md mx-4">
          {isDownloading ? dictionary["downloading"][language] : dictionary["download-orders"][language]}
        </button> */}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <colgroup>
            <col className="w-40" /> {/* Fecha y hora */}
            <col className="w-48" /> {/* Cliente */}
            <col className="w-32" /> {/* Total */}
            <col className="w-32" /> {/* Pago */}
            <col className="w-36" /> {/* Método de pago */}
            <col className="w-36" /> {/* Fulfillment */}
            <col className="w-36" /> {/* Acciones */}
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {dictionary["date-and-time"][language]}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {dictionary["customer"][language]}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {dictionary["total"][language]}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {dictionary["status"][language]}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {dictionary["payment-method"][language]}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {dictionary["fulfillment"][language]}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {orders?.map((order) => (
              <tr key={order?.id || ""} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {convertToZone(
                    format(
                      new Date(order?.created_at || ""),
                      "dd/MM/yyyy HH:mm"
                    )
                  )}
                </td>
                <td
                  className="px-6 py-4 whitespace-nowrap text-sm text-blue-500 truncate"
                  title={`${order?.customer_name || ""} ${
                    order?.customer_las_name || ""
                  }`}
                >
                  {(order?.customer_name || "") +
                    " " +
                    (order?.customer_las_name || "")}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCOP(
                    Number(order?.total_value_with_discount || 0),
                    isDollar
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {order.payment_state == PaidState.PAID ? (
                    <div className="flex items-center">
                      <span className="flex items-center px-3 py-1 text-sm rounded-full bg-green-100 text-green-800">
                        <span className="w-2 h-2 mr-2 bg-green-500 rounded-full"></span>
                        {dictionary["paid-state"][language]}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <span className="flex items-center px-3 py-1 text-sm rounded-full bg-red-100 text-red-800">
                        <span className="w-2 h-2 mr-2 bg-red-500 rounded-full"></span>
                        {order.payment_state === "pending"
                          ? dictionary["pending-state"][language]
                          : dictionary["unpaid-state"][language]}
                      </span>
                    </div>
                  )}
                </td>
                <td
                  className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 truncate"
                  title={
                    paymentProviderLabels[
                      order?.payment_provider || PaymentProvider.CONTRA_ENTREGA
                    ]
                  }
                >
                  <div className="flex items-center">
                    <span className="flex items-center px-3 py-1 text-sm rounded-full bg-[#5050EA]/15 text-[#5050EA]">
                      <span className="w-2 h-2 mr-2 bg-[#5050EA] rounded-full"></span>
                      {order?.payment_provider?.toString().toUpperCase()
                        ? paymentProviderLabels[order?.payment_provider]
                        : "Contra Entrega"}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {order?.logistic_state == LogisticState.IN_PROGRESS ? (
                    <div className="flex items-center">
                      <span className="flex items-center px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-800">
                        {dictionary["fulfilled-state"][language]}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <span className="flex items-center px-3 py-1 text-sm rounded-full bg-yellow-100 text-yellow-800">
                        {dictionary["unfulfilled-state"][language]}
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex items-center gap-4">
                  <a href={`/order/${order.hash_id}`} className="text-blue-500">
                    {dictionary["view-details"][language]}
                  </a>
                  <button onClick={() => handleDeleteOrder(order)}>
                    <IconTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesTable;
